import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { Field, inputClass } from '../components/Field'
import { PhotoUpload } from '../components/PhotoUpload'
import { Turnstile } from '../components/Turnstile'
import { track } from '../lib/analytics'
import { ApiError, api } from '../lib/api'
import type { AddressSuggestion, Category, IntakeAccepted, Photo } from '../lib/types'
import { useSession } from '../lib/session'

/**
 * The intake form (01 §3.2). Nine fields, one page, inline validation.
 *
 * The shape of this screen follows one rule from the spec: *the regular path must feel exactly
 * like a normal contractor site.* No account, no AI, no gate — fill it in, pick a time, done.
 * The workspace is offered afterwards and never before.
 *
 * Photos are attached *after* the estimate exists, because an upload needs something to belong
 * to. The form therefore submits, then reveals the photo step on the same screen rather than
 * making someone wait through an upload before their request is safely recorded.
 */
export function EstimateIntake() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const loadSession = useSession((state) => state.load)

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/catalog/categories'),
    staleTime: 5 * 60_000,
  })

  const preselected = params.get('category')
  const [selected, setSelected] = useState<string[]>(preselected ? [preselected] : [])
  const [address, setAddress] = useState('')
  const [picked, setPicked] = useState<AddressSuggestion | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [created, setCreated] = useState<IntakeAccepted | null>(null)
  const [photos, setPhotos] = useState<Photo[]>([])

  // One key per mount: a retried submit after a network wobble must not create two estimates
  // (05 §5). A new key would defeat the point.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [])
  const hasCategory = selected.length > 0
  const onToken = useCallback((token: string | null) => setTurnstileToken(token), [])

  // 01 §9: the denominator of the funnel. Only the browser knows the form was opened at all;
  // the server never hears about someone who looks and leaves.
  useEffect(() => {
    track('estimate_started', { source: params.get('source') ?? undefined })
  }, [params])

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((c) => c !== id) : [...current, id],
    )
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)
    setErrors({})

    const form = new FormData(event.currentTarget)
    if (selected.length === 0) {
      setErrors({ categories: 'Choose at least one thing you are thinking about.' })
      document.getElementById('categories')?.scrollIntoView({ block: 'center' })
      return
    }

    setBusy(true)
    try {
      const result = await api.post<IntakeAccepted>(
        '/estimates',
        {
          name: form.get('name'),
          email: form.get('email'),
          phone: form.get('phone'),
          address_line1: picked?.address_line1 ?? address,
          address_line2: form.get('address_line2') || null,
          city: picked?.city ?? form.get('city'),
          state: picked?.state ?? form.get('state') ?? 'NJ',
          zip: picked?.zip ?? form.get('zip'),
          lat: picked?.lat ?? null,
          lon: picked?.lon ?? null,
          county: picked?.county ?? null,
          categories: selected,
          description: form.get('description') || null,
          contact_pref: form.get('contact_pref') || 'email',
          consent: form.get('consent') === 'on',
          turnstile_token: turnstileToken,
          source: params.get('source'),
        },
        idempotencyKey,
      )
      await loadSession()
      setCreated(result)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors)
        setFormError(
          Object.keys(err.fieldErrors).length
            ? 'Some fields need attention.'
            : (err.problem.detail ?? err.problem.title),
        )
      } else {
        setFormError('We could not send that. Check your connection and try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (created) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-12">
        <p className="eyebrow">Step 2 of 3</p>
        <h1 className="mt-4 text-3xl">Got it. Now pick a time.</h1>
        <p className="mt-4">{created.message}</p>

        {created.in_service_area === false && (
          <p className="mt-4 rounded-card bg-sand px-4 py-3 text-sm text-ink">
            That address is outside Monmouth County. You can still book — Andrew will confirm the
            travel with you.
          </p>
        )}

        <section className="mt-8 rounded-card border border-line bg-white p-5">
          <h2 className="text-lg">Add photos</h2>
          <p className="mt-1.5 text-sm text-muted">
            Optional, and you can add them later. They give Andrew a head start.
          </p>
          <div className="mt-4">
            <PhotoUpload estimateId={created.id} photos={photos} onChange={setPhotos} />
          </div>
        </section>

        <button
          type="button"
          onClick={() => navigate(`/estimate/${created.id}/schedule`)}
          className="mt-8 w-full rounded-btn bg-blue px-7 py-3.5 font-bold text-white shadow-blue sm:w-auto"
        >
          Pick a visit time
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-12">
      <p className="eyebrow">Step 1 of 3</p>
      <h1 className="mt-4 text-3xl">Tell us what you are thinking about.</h1>
      <p className="mt-3 text-muted">
        Two minutes. No account. Andrew comes out, measures, and gives you one fixed, all-in price
        in writing.
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-8 grid gap-6">
        <fieldset className="m-0 grid gap-3 border-0 p-0" id="categories">
          <legend className="mb-1 p-0 text-sm font-semibold text-ink">
            What are you thinking about?
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {categories.map((category) => {
              const on = selected.includes(category.id)
              return (
                <label
                  key={category.id}
                  className={`flex cursor-pointer gap-3 rounded-card border p-3.5 transition-colors ${
                    on ? 'border-blue bg-blue-tint' : 'border-line bg-white hover:border-line-2'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(category.id)}
                    className="mt-1 size-4 shrink-0 accent-[var(--blue)]"
                  />
                  <span>
                    <span className="block font-semibold text-ink">{category.name}</span>
                    {/* On a phone twelve blurbs are twelve hundred pixels of prose above the
                        first input. The name is what people scan by; the blurb earns its place
                        once the card is chosen, and desktop has room for all of them. */}
                    <span
                      className={
                        on ? 'block text-sm text-muted' : 'hidden text-sm text-muted sm:block'
                      }
                    >
                      {category.blurb}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
          {errors.categories && (
            <p role="alert" className="m-0 text-sm font-medium text-blue-press">
              {errors.categories}
            </p>
          )}
          {!hasCategory && (
            <p className="m-0 text-sm text-muted">Pick at least one and the rest opens up.</p>
          )}
        </fieldset>

        {/* 01 §3.2: one page, progressive disclosure. Everything below is about *this* job, so
            it appears once there is a job — which also halves what a phone shows on arrival.
            Nothing is removed and nothing is a separate step: the form is still one submit. */}
        <div className={hasCategory ? 'grid gap-6' : 'hidden'} aria-hidden={!hasCategory}>
          <h2 className="m-0 text-lg">Where should Andrew come, and who is he asking for?</h2>

          <Field id="name" label="Your name" error={errors.name} required>
            {(props) => <input {...props} name="name" autoComplete="name" className={inputClass} />}
          </Field>

          <div className="grid gap-6 sm:grid-cols-2">
            <Field
              id="email"
              label="Email"
              hint="We send your link here."
              error={errors.email}
              required
            >
              {(props) => (
                <input
                  {...props}
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className={inputClass}
                />
              )}
            </Field>
            <Field id="phone" label="Phone" error={errors.phone} required>
              {(props) => (
                <input
                  {...props}
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(732) 555-0134"
                  className={inputClass}
                />
              )}
            </Field>
          </div>

          <AddressAutocomplete
            value={address}
            onChange={(value) => {
              setAddress(value)
              setPicked(null)
            }}
            onSelect={(suggestion) => {
              setPicked(suggestion)
              setAddress(suggestion.address_line1)
            }}
            error={errors.address_line1}
          />

          <div className="grid gap-6 sm:grid-cols-[2fr_1fr_1fr]">
            <Field id="city" label="Town" error={errors.city} required>
              {(props) => (
                <input
                  {...props}
                  name="city"
                  autoComplete="address-level2"
                  defaultValue={picked?.city}
                  key={picked?.city ?? 'city'}
                  className={inputClass}
                />
              )}
            </Field>
            <Field id="state" label="State" error={errors.state} required>
              {(props) => (
                <input
                  {...props}
                  name="state"
                  autoComplete="address-level1"
                  maxLength={2}
                  defaultValue={picked?.state ?? 'NJ'}
                  key={picked?.state ?? 'state'}
                  className={inputClass}
                />
              )}
            </Field>
            <Field id="zip" label="ZIP" error={errors.zip} required>
              {(props) => (
                <input
                  {...props}
                  name="zip"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={10}
                  defaultValue={picked?.zip}
                  key={picked?.zip ?? 'zip'}
                  className={inputClass}
                />
              )}
            </Field>
          </div>

          <Field id="address_line2" label="Apartment, unit, etc." error={errors.address_line2}>
            {(props) => (
              <input
                {...props}
                name="address_line2"
                autoComplete="address-line2"
                className={inputClass}
              />
            )}
          </Field>

          <Field
            id="description"
            label="Tell us more"
            hint="What you have in mind, what is bothering you, anything Andrew should know."
            error={errors.description}
          >
            {(props) => <textarea {...props} name="description" rows={4} className={inputClass} />}
          </Field>

          <Field id="contact_pref" label="How should Andrew reach you?" required>
            {(props) => (
              <select {...props} name="contact_pref" defaultValue="email" className={inputClass}>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            )}
          </Field>

          <label className="flex gap-3 rounded-card border border-line bg-white p-3.5 text-sm">
            <input
              type="checkbox"
              name="consent"
              required
              className="mt-0.5 size-4 shrink-0 accent-[var(--blue)]"
            />
            <span>
              I agree to the{' '}
              <a href="/privacy" className="text-blue-deep underline-offset-4 hover:underline">
                privacy policy
              </a>
              , and I understand my photos may be used to generate design previews.
            </span>
          </label>

          <Turnstile onToken={onToken} />

          {formError && (
            <p role="alert" className="m-0 rounded-btn bg-blue-tint px-3.5 py-3 text-sm text-ink">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="rounded-btn bg-blue px-7 py-3.5 text-base font-bold text-white shadow-blue disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Continue to pick a time'}
          </button>
        </div>
      </form>
    </div>
  )
}
